import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import {Modal, ModalBody, ModalFooter, ModalHeader} from "../Modal";
import RadioButton from "../RadioButton";
import CustomButton from "../CustomButton";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import successIcon from "url:shared-assets/icons/green/badge-check-filled.svg";

interface NpsInterface {
  nps: number | null;
  sean_ellis: number | null;
  comment: string;
}

interface Props {
  setShowSurveyType: Function;
}

const UserFeedbackModal = (props: Props) => {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dataIsSent, setDataIsSent] = useState<boolean>(false);
  const [formData, setFormData] = useState<NpsInterface>({
    nps: null,
    sean_ellis: null,
    comment: "",
  });

  const goToNextStep = () => {
    if (currentIndex < 3) {
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, 500);
    }
  };

  const goBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const sendData = () => {
    if (dataIsSent) {
      props.setShowSurveyType(null);
      return;
    }
    try {
      api.post("client/nps", formData).then((response) => {
        setDataIsSent(true);
        props.setShowSurveyType(null);
      });
    } catch (error) {
      console.error("Error sending data:", error);
    }
  };

  const renderStep1 = () => {
    const text1 =
      lang === "sv"
        ? "Ta 15 sekunder för att svara på en snabb, anonym undersökning."
        : "Take 15 seconds to answer a quick, anonymous survey.";

    const text2 = lang === "sv" ? "Fråga 1 av 2" : "Question 1 of 2";

    const text3 =
      lang === "sv"
        ? "Hur sannolikt är det att du skulle rekommendera Zebrain’s plattform till en vän eller kollega?"
        : "How likely are you to recommend Zebrain’s platform to a friend or colleague?";

    const options = 11;
    return (
      <div className="text-left">
        <p className="text-gray-300">{text1}</p>
        <hr className="w-full border-t border-indigo-700 mt-4" />

        <p className="font-medium mt-4">{text2}</p>
        <p className="text-gray-300 my-1">{text3}</p>
        <div className="w-full">
          <div className="flex flex-row flex-nowrap  items-center justify-center mb-2 border border-indigo-700 rounded-md">
            {[...Array(options)].map((e, i) => (
              <div
                className={`py-2 border-indigo-700 grow items-center text-center basis-1
                    ${
                      i === options - 1
                        ? "border-l"
                        : i < options - 2 && "border-r"
                    } 
                    ${
                      formData.nps === i
                        ? "bg-primary-500 text-indigo-1000"
                        : "bg-transparent text-white hover:bg-indigo-800 hover:text-white"
                    }
                     cursor-pointer
                    `}
                onClick={() => {
                  setFormData({ ...formData, nps: i });
                  goToNextStep();
                }}
                key={i}
              >
                <p>{i}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-row justify-between z-title3 text-gray-500 leading-none">
            <p>{lang === "sv" ? "Inte alls sannolikt" : "Not at all likely"}</p>

            <p>{lang === "sv" ? "Extremt sannolikt" : "Extremely likely"}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    const text1 = lang === "sv" ? "Fråga 2 av 2" : "Question 2 of 2";

    const text2 =
      lang === "sv"
        ? "Hur skulle du känna om du inte längre kunde använda Zebrains plattform?"
        : "How would you feel if you could no longer use Zebrain’s platform?";

    const very_disappointed_sub =
      lang === "sv"
        ? "Den är viktig för mig, och jag skulle verkligen sakna den."
        : "It's important to me, and I would really miss it.";
    const somewhat_disappointed_sub =
      lang === "sv"
        ? "Jag gillar det men jag skulle klara mig utan det."
        : "I like it, but I could manage without it.";
    const not_disappointed_sub =
      lang === "sv"
        ? "Jag använder det knappt eller tycker inte att det är särskilt värdefullt."
        : "I barely use it or don't find it very valuable.";

    return (
      <div className="flex flex-col gap-1 h-full text-left">
        <p className="font-medium">{text1}</p>
        <div className="flex flex-col gap-3  h-full">
          <p className="text-gray-300">{text2}</p>
          <RadioButton
            id="very_disappointed"
            name="very_disappointed"
            label={`😢 ${t("very-disappointed")}`}
            value={"very_disappointed"}
            checked={formData.sean_ellis === 3}
            onChange={() => {
              setFormData({ ...formData, sean_ellis: 3 });
              goToNextStep();
            }}
            onClick={(checked: boolean) => {
              if (checked) {
                goToNextStep();
              }
            }}
            subtitle={very_disappointed_sub}
          />
          <RadioButton
            id="somewhat_disappointed"
            name="somewhat_disappointed"
            label={`😐 ${t("somewhat-disappointed")}`}
            value={"somewhat_disappointed"}
            checked={formData.sean_ellis === 2}
            onChange={() => {
              setFormData({ ...formData, sean_ellis: 2 });
              goToNextStep();
            }}
            onClick={(checked: boolean) => {
              if (checked) {
                goToNextStep();
              }
            }}
            subtitle={somewhat_disappointed_sub}
          />
          <RadioButton
            id="not_disappointed"
            name="not_disappointed"
            label={`😌 ${t("not-disappointed")}`}
            value={"not_disappointed"}
            checked={formData.sean_ellis === 1}
            onChange={() => {
              setFormData({ ...formData, sean_ellis: 1 });
              goToNextStep();
            }}
            onClick={(checked: boolean) => {
              if (checked) {
                goToNextStep();
              }
            }}
            subtitle={not_disappointed_sub}
          />
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const text1 =
      i18n.language === "sv"
        ? "Vad skulle vi kunna göra bättre?"
        : "What could we do better?";

    const text2 = i18n.language === "sv" ? "valfritt" : "optional";

    const text3 =
      i18n.language === "sv" ? "Snälla förbättra..." : "Please improve...";

    return (
      <div className="flex flex-col gap-2 text-left h-full">
        <p className="font-medium">
          {text1}
          <span className="text-gray-400"> ({text2})</span>
        </p>
        <textarea
          className="z-input z-800 h-40 w-full bg-indigo-900"
          placeholder={text3}
          value={formData.comment}
          onChange={(e) => {
            const chars = e.target.value.length;
            if (chars <= 500) {
              setFormData((prev: any) => ({
                ...prev,
                comment: e.target.value,
              }));
            }
          }}
        />
      </div>
    );
  };

  const renderStep4 = () => {
    const text1 =
      i18n.language === "sv"
        ? "Din feedback hjälper oss att bli bättre!"
        : "Your feedback helps us improve!";
    return (
      <div className="flex flex-col gap-3 text-left h-full">
        <img
          src={successIcon}
          alt="Success"
          className="w-12 h-12 mb-4 text-center mx-auto"
        />
        <p className="text-gray-300 text-left">{text1}</p>
      </div>
    );
  };

  const showContent = () => {
    if (currentIndex === 1) {
      return renderStep2();
    } else if (currentIndex === 2) {
      return renderStep3();
    } else if (currentIndex === 3) {
      return renderStep4();
    } else {
      return renderStep1();
    }
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={() => {
          sendData();
        }}
        ignoreOutsideClick={true}
      >
        <ModalHeader
          onClose={() => {
            sendData();
          }}
        >
          <div className="z-title1">
            {currentIndex === 3
              ? lang === "sv"
                ? "Tack för dina svar"
                : "Thanks for your response"
              : t("user-feedback-modal-header")}
          </div>
        </ModalHeader>
        <ModalBody>{showContent()}</ModalBody>
        {currentIndex !== 0 && (
          <ModalFooter>
            {currentIndex === 2 && (
              <div className="flex flex-col justify-between w-full gap-2">
                <CustomButton
                  onClick={() => {
                    sendData();
                    goToNextStep();
                  }}
                >
                  {t("submit-feedback")}
                </CustomButton>
                <CustomButton buttonType="outlineWhiteBase" onClick={goBack}>
                  {t("back")}
                </CustomButton>
              </div>
            )}
            {currentIndex === 1 && (
              <div className="flex flex-col justify-between w-full gap-2">
                {
                  formData.sean_ellis !== null &&
                    <CustomButton
                        onClick={() => {
                          goToNextStep();
                        }}
                    >
                      {t("next")}
                    </CustomButton>
                }
                <CustomButton buttonType="outlineWhiteBase" onClick={goBack}>
                  {t("back")}
                </CustomButton>
              </div>
            )}
            {currentIndex === 3 && (
              <CustomButton onClick={() => props.setShowSurveyType(null)}>
                {t("keep-growing")}
              </CustomButton>
            )}
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

export default UserFeedbackModal;
